/**
 * TickerManager - Handles news ticker scrolling animation
 * Extracted from main.ts for better code organization
 */

import { TICKER_SPEED, MAX_TICKER_ITEMS } from '../utils/Constants';

export type NewsType = 'important' | 'critical' | 'market' | 'flavor';

export class TickerManager {
  private tickerPosition = 0;
  private tickerPaused = false;
  private lastTickerUpdate = 0;
  private tickerElement: HTMLElement | null = null;
  private containerElement: HTMLElement | null = null;

  constructor() {
    this.tickerElement = document.getElementById('ticker-content');
    this.containerElement = document.getElementById('news-ticker');
  }

  /**
   * Setup ticker scrolling and hover pause behavior
   */
  public setup(): void {
    if (!this.tickerElement || !this.containerElement) return;

    // Start position off-screen to the right
    const containerWidth = this.containerElement.offsetWidth;
    this.tickerPosition = containerWidth;
    this.tickerElement.style.transform = `translateX(${this.tickerPosition}px)`;

    // Pause on hover
    this.containerElement.addEventListener('mouseenter', () => {
      this.tickerPaused = true;
    });
    this.containerElement.addEventListener('mouseleave', () => {
      this.tickerPaused = false;
    });

    this.lastTickerUpdate = performance.now();
  }

  /**
   * Update ticker scroll position - called from game loop
   */
  public update(timestamp: number): void {
    if (this.tickerPaused) {
      this.lastTickerUpdate = timestamp;
      return;
    }

    if (!this.tickerElement) return;

    const deltaTime = (timestamp - this.lastTickerUpdate) / 1000;
    this.lastTickerUpdate = timestamp;

    // Move ticker left
    this.tickerPosition -= TICKER_SPEED * deltaTime;

    // Get ticker width (all items)
    const tickerWidth = this.tickerElement.scrollWidth;

    // If ticker has scrolled completely off screen to the left, reset
    if (this.tickerPosition < -tickerWidth) {
      // Remove old items that have scrolled past, keep recent ones
      const items = this.tickerElement.querySelectorAll('.ticker-item');
      const itemsToKeep = Math.min(items.length, MAX_TICKER_ITEMS);
      while (items.length > itemsToKeep && this.tickerElement.firstChild) {
        this.tickerElement.removeChild(this.tickerElement.firstChild);
      }

      // Reset position to start from right edge
      if (this.containerElement) {
        this.tickerPosition = this.containerElement.offsetWidth;
      }
    }

    this.tickerElement.style.transform = `translateX(${this.tickerPosition}px)`;
  }

  /**
   * Add a news item to the ticker
   */
  public addItem(text: string, type: NewsType = 'flavor'): void {
    if (!this.tickerElement) return;

    const item = document.createElement('span');
    item.className = `ticker-item ticker-${type}`;
    item.textContent = text;
    this.tickerElement.appendChild(item);
  }

  /**
   * Check if ticker is paused
   */
  public isPaused(): boolean {
    return this.tickerPaused;
  }
}
